import React, { useState, useEffect } from 'react';
import { Upload, Trash2 } from 'lucide-react';
import supabase from '../lib/supabase';

interface CommunityImageUploadProps {
  initialImage?: string | null;
  communityId: string;
  onUploadComplete: (url: string) => void;
  className?: string;
}

export default function CommunityImageUpload({ 
  initialImage,
  communityId,
  onUploadComplete,
  className = ''
}: CommunityImageUploadProps) {
  const [isUploading, setIsUploading] = useState(false);
  const [coverImage, setCoverImage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Helper function to check if URL is from Supabase storage
  const isSupabaseStorageUrl = (url: string): boolean => {
    return url.includes('supabase') && url.includes('/storage/');
  };

  // Helper function to extract storage path from public URL
  const getStoragePathFromPublicUrl = (url: string) => {
    const split = url.split('/object/public/community-covers/');
    return split.length === 2 ? split[1].split('?')[0] : null; // Remove query params
  };

  // Initialize cover image with cache busting
  useEffect(() => {
    if (initialImage) {
      const cacheBustedUrl = isSupabaseStorageUrl(initialImage)
        ? `${initialImage.split('?')[0]}?v=${Date.now()}`
        : initialImage;
      setCoverImage(cacheBustedUrl);
    }
  }, [initialImage]);

  const handleImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      setIsUploading(true);
      setError(null);

      // Validate file size (5MB limit)
      if (file.size > 5 * 1024 * 1024) {
        throw new Error('Image size must be less than 5MB');
      }

      // Validate file type
      if (!file.type.startsWith('image/')) {
        throw new Error('File must be an image');
      }

      // Remove old image if it exists
      if (coverImage && isSupabaseStorageUrl(coverImage)) {
        const oldFilePath = getStoragePathFromPublicUrl(coverImage);
        if (oldFilePath) {
          await supabase.storage
            .from('community-covers')
            .remove([oldFilePath]);
        }
      }

      // Create unique file path
      const fileExt = file.name.split('.').pop();
      const filePath = `${communityId}/cover.${fileExt}`;

      // Upload file with proper options
      const { error: uploadError } = await supabase.storage
        .from('community-covers')
        .upload(filePath, file, {
          upsert: true,
          cacheControl: '3600',
          contentType: file.type,
        });

      if (uploadError) throw uploadError;

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('community-covers')
        .getPublicUrl(filePath);

      // Add cache busting parameter to force browser refresh
      const cacheBustedUrl = isSupabaseStorageUrl(publicUrl) 
        ? `${publicUrl}?v=${Date.now()}` 
        : publicUrl;

      // Update local state
      setCoverImage(cacheBustedUrl);
      
      // Notify parent with cache-busted URL
      onUploadComplete(cacheBustedUrl);

    } catch (err: any) {
      console.error('Error uploading image:', err);
      setError(err.message);
    } finally {
      setIsUploading(false);
    }
  };

  const handleDelete = async () => {
    try {
      setError(null);

      // Only try to delete from storage if it's a Supabase URL
      if (coverImage && isSupabaseStorageUrl(coverImage)) {
        const filePath = getStoragePathFromPublicUrl(coverImage);
        
        if (filePath) {
          // Delete from storage
          const { error: deleteError } = await supabase.storage
            .from('community-covers')
            .remove([filePath]);

          if (deleteError) throw deleteError;
        }
      }

      // Update local state
      setCoverImage(null);
      
      // Notify parent
      onUploadComplete('');

    } catch (err: any) {
      console.error('Error deleting image:', err);
      setError(err.message);
    }
  };

  return (
    <div className="relative w-full">
      {error && (
        <div className="absolute top-0 left-0 right-0 bg-red-500 text-white p-2 text-sm text-center rounded-t-lg z-10">
          {error}
        </div>
      )}

      {coverImage ? (
        <div className="relative">
          <img
            src={coverImage}
            alt="Community cover"
            className={className}
            key={coverImage} // Force re-render when URL changes
          />
          <button
            onClick={handleDelete}
            className="absolute top-2 left-2 p-1 bg-primary text-surface-light rounded-full hover:bg-secondary transition-colors z-10"
            title="Remove cover image"
            disabled={isUploading}
          >
            <Trash2 className="w-4 h-4" />
          </button>
          
          {/* Add upload button overlay for editing */}
          <label className="absolute bottom-2 right-2 p-2 bg-primary text-surface-light rounded-full hover:bg-secondary transition-colors cursor-pointer z-10">
            <Upload className="w-4 h-4" />
            <input
              type="file"
              className="hidden"
              accept="image/*"
              onChange={handleImageChange}
              disabled={isUploading}
            />
          </label>
        </div>
      ) : (
        <label className="w-full h-32 flex flex-col items-center justify-center border-2 border-primary border-dashed rounded-t-xl relative cursor-pointer hover:border-secondary transition-colors">
          <Upload className="w-8 h-8 text-primary mb-2 hover:text-secondary transition-colors" />
          <span className="text-sm text-primary hover:text-secondary transition-colors">Upload cover image</span>
          <input
            type="file"
            className="hidden"
            accept="image/*"
            onChange={handleImageChange}
            disabled={isUploading}
          />
        </label>
      )}

      {isUploading && (
        <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center rounded-lg z-20">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-white"></div>
        </div>
      )}
    </div>
  );
}